/**
 * 通用批量操作栏组件
 *
 * 统一的批量操作 UI 组件，适用于任何需要批量操作的列表页面。
 * 组件自带 fixed 悬浮定位（居中于页面顶部 Header 下方），不占文档流空间，
 * 选中行时出现不会推动 Table 下移。
 *
 * 使用方式（消费方无需额外 wrapper）：
 * @example
 * {selectedRowKeys.length > 0 && (
 *     <BatchActionsBar
 *         selectedCount={selectedRowKeys.length}
 *         onSelectAll={handleSelectAllVisible}
 *         onClearSelection={clearSelection}
 *         actions={[
 *             { key: 'delete', label: '批量删除', icon: <DeleteOutlined />, danger: true, onClick: handleBatchDelete },
 *             { key: 'update', label: '批量更新', icon: <EditOutlined />, onClick: handleBatchUpdate },
 *         ]}
 *     />
 * )}
 */

import React from "react";
import { Button, Space, Divider, Tooltip, Typography, theme } from "antd";
import { CheckSquareOutlined, CloseOutlined } from "@ant-design/icons";

const { Text } = Typography;

/**
 * 批量操作栏组件
 *
 * 内置 fixed 悬浮定位，居中于页面顶部，不占文档流空间。
 * 消费方无需手动包裹 batch-actions-sticky-wrapper。
 *
 * @param {Object} props
 * @param {number} props.selectedCount - 已选择的项目数量
 * @param {string} props.itemName - 项目名称（如 "条记录"、"航班"、"数据源"）
 * @param {Function} props.onSelectAll - 全选当前页回调
 * @param {Function} props.onClearSelection - 取消选择回调
 * @param {Array<Object>} props.actions - 操作按钮配置数组
 *   - key: 唯一标识
 *   - label: 按钮文本
 *   - icon: 图标
 *   - danger: 是否为危险操作
 *   - loading: 是否加载中
 *   - disabled: 是否禁用
 *   - onClick: 点击回调
 *   - tooltip: 提示文本
 *   - type: 按钮类型（primary, default, text, link）
 *   - ghost: 是否幽灵按钮
 *   - render: 自定义渲染函数，如果提供则忽略其他属性
 * @param {React.ReactNode} props.extra - 额外的内容，渲染在右侧
 * @param {boolean} props.visible - 是否强制显示操作栏，默认按 selectedCount 判断
 * @param {boolean} props.showSelectionSummary - 是否展示选择摘要，默认 selectedCount > 0
 * @param {React.ReactNode} props.children - 追加操作行，渲染在主批量操作行下方
 */
const BatchActionsBar = ({
    selectedCount = 0,
    itemName = "条记录",
    onSelectAll,
    onClearSelection,
    actions = [],
    extra,
    visible,
    showSelectionSummary = selectedCount > 0,
    children,
}) => {
    const { token } = theme.useToken();
    const shouldShow = visible ?? selectedCount > 0;
    const hasPrimaryRow = showSelectionSummary || actions.length > 0 || extra;

    // 默认没有选中项时不显示；允许消费方用 visible 承载同类浮动操作行。
    if (!shouldShow) {
        return null;
    }

    return (
        <div
            className="batch-actions-sticky-wrapper"
            style={{
                position: "fixed",
                top: 72,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1000,
                animation: "batchBarSlideIn 0.2s ease-out",
            }}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    background: "linear-gradient(135deg, #fafcff 0%, #f5f8ff 100%)",
                    padding: "10px 20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
                    border: "1px solid rgba(24, 144, 255, 0.12)",
                    gap: 8,
                }}
            >
                {hasPrimaryRow && (
                    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                        {/* 第一部分：选择状态与管理 */}
                        {showSelectionSummary && (
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        lineHeight: 1.2,
                                        borderRight: "1px solid rgba(0,0,0,0.06)",
                                        paddingRight: 16,
                                    }}
                                >
                                    <Text style={{ fontSize: 12, color: "#888" }}>已选择</Text>
                                    <Text style={{ fontSize: 16, fontWeight: 600, color: token.colorPrimary }}>
                                        {selectedCount} <span style={{ fontSize: 12, fontWeight: 400, color: "#666" }}>{itemName}</span>
                                    </Text>
                                </div>

                                {(onSelectAll || onClearSelection) && (
                                    <Space size={4}>
                                        {onSelectAll && (
                                            <Tooltip title="选中当前页所有项目">
                                                <Button type="text" size="small" icon={<CheckSquareOutlined />} onClick={onSelectAll}>
                                                    全选
                                                </Button>
                                            </Tooltip>
                                        )}
                                        {onClearSelection && (
                                            <Tooltip title="取消当前所有选择">
                                                <Button type="text" size="small" danger icon={<CloseOutlined />} onClick={onClearSelection}>
                                                    取消
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </Space>
                                )}
                            </div>
                        )}

                        {/* 第二部分：操作按钮 */}
                        {actions.length > 0 && (
                            <>
                                {showSelectionSummary ? <Divider type="vertical" style={{ height: 24, margin: 0 }} /> : null}
                                <Space size={8} wrap>
                                    {actions.map((action) => {
                                        // 如果提供了自定义渲染函数，使用它
                                        if (action.render) {
                                            return <React.Fragment key={action.key}>{action.render()}</React.Fragment>;
                                        }

                                        const button = (
                                            <Button
                                                key={action.key}
                                                size="small"
                                                type={action.type || "primary"}
                                                ghost={action.ghost !== false}
                                                danger={action.danger}
                                                icon={action.icon}
                                                loading={action.loading}
                                                disabled={action.disabled}
                                                onClick={action.onClick}
                                                style={{ fontSize: "12px", ...action.style }}
                                            >
                                                {action.label}
                                                {action.showCount !== false && ` (${selectedCount})`}
                                            </Button>
                                        );

                                        if (action.tooltip) {
                                            return (
                                                <Tooltip key={action.key} title={action.tooltip}>
                                                    {button}
                                                </Tooltip>
                                            );
                                        }

                                        return button;
                                    })}
                                </Space>
                            </>
                        )}

                        {/* 第三部分：额外内容 */}
                        {extra && <div style={{ marginLeft: "auto" }}>{extra}</div>}
                    </div>
                )}
                {children ? <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>{children}</div> : null}
            </div>
        </div>
    );
};

export default BatchActionsBar;
